import React, { useState } from 'react';
import { AlertCircleIcon, ArrowLeftIcon, CheckCircleIcon, XIcon, CalendarIcon } from 'lucide-react';
interface BulkPaymentPreviewProps {
  data: any[];
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}
export function BulkPaymentPreview({
  data,
  onBack,
  onConfirm,
  isSubmitting
}: BulkPaymentPreviewProps) {
  const [selectedRows, setSelectedRows] = useState<number[]>([...Array(data.length).keys()]);
  const toggleRow = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };
  const toggleAllRows = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...Array(data.length).keys()]);
    }
  };
  const hasErrors = data.some(row => {
    return !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
  });
  // Calculate totals
  const selectedData = data.filter((_, index) => selectedRows.includes(index));
  const totalAmount = selectedData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const currency = data[0]?.currency || 'USD';
  return <div className="space-y-6" data-id="element-1639">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1640">
        <div className="flex justify-between items-center mb-6" data-id="element-1641">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-1642">Preview Batch</h3>
        </div>
        {hasErrors && <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-start mb-6" data-id="element-1643">
            <AlertCircleIcon size={18} className="mt-0.5 mr-3 flex-shrink-0" data-id="element-1644" />
            <div data-id="element-1645">
              <p className="text-sm font-medium" data-id="element-1646">Some rows have errors</p>
              <p className="text-sm mt-1" data-id="element-1647">
                Please fix the issues highlighted in red before proceeding. All
                required fields must be completed, and payment amounts must be
                valid numbers greater than zero.
              </p>
            </div>
          </div>}
        <div className="border border-gray-200 rounded-md overflow-hidden mb-6" data-id="element-1648">
          <div className="max-h-[400px] overflow-auto" data-id="element-1649">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1650">
              <thead className="bg-gray-50 sticky top-0" data-id="element-1651">
                <tr data-id="element-1652">
                  <th className="px-4 py-3 text-left" data-id="element-1653">
                    <div className="flex items-center" data-id="element-1654">
                      <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.length === data.length} onChange={toggleAllRows} data-id="element-1655" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1656">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1657">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1658">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1659">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1660">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1661">
                {data.map((row, index) => {
                const hasError = !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
                return <tr key={index} className={hasError ? 'bg-red-50' : ''} data-id="element-1662">
                      <td className="px-4 py-3" data-id="element-1663">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.includes(index)} onChange={() => toggleRow(index)} disabled={hasError} data-id="element-1664" />
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.customerName ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1665">
                        {row.customerName || 'Missing name'}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-1666">
                        <div className={!row.email ? 'text-red-600' : 'text-gray-900'} data-id="element-1667">
                          {row.email || 'Missing email'}
                        </div>
                        {row.phone && <div className="text-gray-500 text-xs mt-1" data-id="element-1668">
                            {row.phone}
                          </div>}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium ${!row.amount || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1669">
                        {!row.amount || isNaN(parseFloat(row.amount)) ? 'Invalid amount' : parseFloat(row.amount) <= 0 ? 'Amount must be > 0' : `${row.currency || currency} ${parseFloat(row.amount).toFixed(2)}`}
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.description ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1670">
                        {row.description || 'Missing description'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1671">
                        {row.reference || '-'}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-4 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1672">
          <CalendarIcon size={14} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-1673" />
          <span data-id="element-1674">
            Payment links will expire in{' '}
            <span className="font-medium text-gray-700" data-id="element-1675">30 days</span> from
            creation
          </span>
        </div>

        <div className="flex justify-between items-center" data-id="element-1676">
          <div data-id="element-1677">
            <p className="text-sm text-gray-600" data-id="element-1678">
              <span className="font-medium" data-id="element-1679">{selectedRows.length}</span> of{' '}
              <span className="font-medium" data-id="element-1680">{data.length}</span> payment
              requests selected
            </p>
            <p className="text-sm font-medium text-gray-800 mt-1" data-id="element-1681">
              Total: {currency} {totalAmount.toFixed(2)}
            </p>
          </div>
          <div className="flex space-x-4" data-id="element-1682">
            <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1683">
              Back
            </button>
            <button type="button" onClick={onConfirm} disabled={selectedRows.length === 0 || hasErrors || isSubmitting} className={`
                px-4 py-2 font-medium rounded-lg transition-colors
                ${selectedRows.length === 0 || hasErrors || isSubmitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#F7941D] hover:bg-[#D97B06] text-white'}
              `} data-id="element-1684">
              {isSubmitting ? 'Creating Batch...' : 'Create Payment Requests'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}